<template>
    <a
        :href="menuItem.link"
        data-test="page-menu-link"
        class="page-menu-link flex grow basis-0 flex-col items-center py-3 rounded-md h-[130px]"
        :class="{
            'active': isActive,
            'hovered': isFirstLevelMenuItemHovered || showSecondLevelMenuDropdown
        }"
        @click.prevent="handlePrimaryItemClick(menuItem)"
        @mouseover="handleFirstLevelMenuItemMouseEnter"
        @mouseleave="handleFirstLevelMenuItemMouseLeave"
        @focusin="handleFirstLevelMenuItemFocus"
        @focusout="handleFirstLevelMenuItemBlur"
    >
        <div class="menu-icon-wrapper flex items-center justify-center rounded-full">
            <span
                class="icon-large icon-menu-item"
                data-test="page-menu-link-icon"
                v-html="menuItem.icon"
            />
        </div>
        <span
            class="page-menu-link-label font-semibold text-center px-2 flex grow items-center text-sm"
            :class="{ 'vertically-centered': shouldCenterVertically(menuItem.label) }"
            data-test="page-menu-link-label"
        >
            {{ menuItem.label }}
        </span>

        <div class="menu-dropdown">
            <div
                v-if="!isInModal && hasSecondLevelItems && showSecondLevelMenuDropdown"
                id="image-nav-dropdown"
                class="dropdown-menu image-nav-dropdown second-level"
                :class="[`first-level-index-${firstLevelMenuIndex}`, { 'second-level-left-side': !isEnoughSpaceForSecondLevel }]"
                tabindex="0"
                @mouseover="handleSecondLevelMenuMouseEnter"
                @mouseleave="handleSecondLevelMenuMouseLeave"
                @focusin="handleSecondLevelMenuFocus"
                @focusout="handleSecondLevelMenuBlur"
            >
                <div
                    v-for="(itemSecondLevel, index) in menuItem.items"
                    :key="itemSecondLevel.link"
                    ref="secondLevelItem"
                    role="link"
                    tabindex="0"
                    :aria-label="menuItem.label"
                    data-test="page-menu-link-secondary"
                    :class="{
                        'hovered': isSecondLevelItemHovered(itemSecondLevel),
                        'focused-second-level-item': focusedSecondLevelIndex === index
                    }"
                    class="dropdown-item menu-second-level cursor-pointer"
                    @click.stop.prevent="handleSecondaryDropdownItemClick(itemSecondLevel)"
                    @mouseover="handleSecondLevelMenuItemMouseEnter(itemSecondLevel, index)"
                    @focusin="handleSecondLevelMenuItemFocus(itemSecondLevel, index)"
                >
                    <div class="dropdown-icon-wrap flex items-center justify-center rounded-full">
                        <span
                            class="dropdown-icon"
                            v-html="itemSecondLevel.icon"
                        />
                    </div>
                    <div class="dropdown-item-title">
                        {{ itemSecondLevel.label }}
                    </div>
                    <icon-right-arrow class="right-icon" />
                </div>

                <div class="menu-dropdown">
                    <div
                        v-if="hasThirdLevelItems"
                        id="image-nav-dropdown"
                        tabindex="0"
                        class="dropdown-menu image-nav-dropdown third-level"
                        :class="{ 'third-level-left-side': !isEnoughSpaceForThirdLevel }"
                        :style="`top: ${hoveredSecondLevelItemIndex * 51}px`"
                        @mouseover="isThirdLevelMenuHovered = true"
                        @mouseleave="isThirdLevelMenuHovered = false"
                        @focusin="isThirdLevelMenuHovered = true"
                        @focusout="isThirdLevelMenuHovered = false"
                    >
                        <div
                            v-for="(itemThirdLevel, index) in hoveredSecondLevelItem?.items"
                            :key="itemThirdLevel.link"
                            ref="thirdLevelItem"
                            role="link"
                            tabindex="0"
                            :aria-label="menuItem.label"
                            data-test="page-menu-link-tertiary"
                            :class="{
                                'hovered': isThirdLevelItemHovered(itemThirdLevel),
                                'focused-third-level-item': focusedThirdLevelIndex === index
                            }"
                            class="dropdown-item menu-third-level cursor-pointer"
                            @focusin="handleThirdLevelMenuItemFocus(itemThirdLevel, index)"
                            @click.stop.prevent="handleTertiaryDropdownItemClick(itemThirdLevel)"
                        >
                            <div class="dropdown-icon-wrap flex items-center justify-center rounded-full">
                                <span
                                    class="dropdown-icon"
                                    v-html="itemThirdLevel.icon"
                                />
                            </div>
                            <div class="dropdown-item-title">
                                {{ itemThirdLevel.label }}
                            </div>
                            <icon-right-arrow class="right-icon" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </a>
</template>

<script setup lang="ts">
    import type { INavItem } from '~/types/types';
    import { onMounted, onUnmounted, onUpdated, type PropType, ref, computed, nextTick } from 'vue';
    import { useAccountStore } from '~/stores/accountStore';
    import IconRightArrow from '~/components/icons/IconRightArrow.vue';
    import { useNavigationStore } from '~/stores/navigationStore';
    import { useRoute } from 'vue-router';
    import { EMenuType } from '~/types/enums';

    const props = defineProps({
        menuItem: {
            type: Object as PropType<INavItem>,
            required: true,
        },
        isInModal: {
            type: Boolean,
            required: false,
            default: false
        },
        isActive: {
            type: Boolean,
            required: false,
            default: false
        },
        firstLevelMenuIndex: {
            type: Number,
            required: false,
            default: 0
        }
    });

    const navigationStore = useNavigationStore();
    const route = useRoute();
    const { buttonColor, buttonTextColor } = useAccountStore().state;
    const emit = defineEmits(['primaryItemClick', 'dropdownItemClick']);

    const isFirstLevelMenuItemHovered = ref(false);
    const isSecondLevelMenuHovered = ref(false);
    const isThirdLevelMenuHovered = ref(false);
    const showSecondLevelMenuDropdown = ref(false);
    const hoveredSecondLevelItem = ref<INavItem | null>(null);
    const hoveredThirdLevelItem = ref<INavItem | null>(null);
    const hoveredSecondLevelItemIndex = ref(0);
    const isEnoughSpaceForSecondLevel = ref(true);
    const isEnoughSpaceForThirdLevel = ref(true);
    const focusedSecondLevelIndex = ref(-1);
    const focusedThirdLevelIndex = ref(-1);
    const secondLevelItem = ref(null);
    const thirdLevelItem = ref(null);

    const hasSecondLevelItems = computed(() => {
        return props.menuItem.items.length > 0;
    });

    const hasThirdLevelItems = computed(() => {
        return hoveredSecondLevelItem.value && hoveredSecondLevelItem.value.items.length > 0;
    });

    const shouldCenterVertically = (label: string) => {
        const firstWord = label.split(' ')[0];
        return label.length < 17 || firstWord.length > 15;
    };

    const isActiveNavItem = (itemLink: string) => {
        return route.path === itemLink;
    };

    const processMenuLink = (menuItem: INavItem) => {
        if (menuItem.type !== EMenuType.CUSTOM) {
            return navigateTo(menuItem.link);
        }
        navigateTo(menuItem.link, { external: true, open: { target: '_blank' } });
    };


    const resetItemsState = () => {
        isSecondLevelMenuHovered.value = isThirdLevelMenuHovered.value = showSecondLevelMenuDropdown.value = false;
        hoveredSecondLevelItem.value = null;
        hoveredSecondLevelItemIndex.value = 0;
    };

    const handlePrimaryItemClick = (menuItem: INavItem) => {
        resetItemsState();
        emit('primaryItemClick', menuItem);
        processMenuLink(menuItem);
    };

    const handleSecondaryDropdownItemClick = (menuItem: INavItem) => {
        resetItemsState();
        showSecondLevelMenuDropdown.value = false;
        emit('dropdownItemClick', menuItem);

        if (menuItem.type !== EMenuType.CUSTOM && !isActiveNavItem(menuItem.link)) {
            navigationStore.setActiveNavItems(props.menuItem, 1);
            navigationStore.setActiveNavItems(menuItem, 2);
        }
        processMenuLink(menuItem);
    };

    const handleTertiaryDropdownItemClick = (menuItem: INavItem) => {
        resetItemsState();
        showSecondLevelMenuDropdown.value = false;
        emit('dropdownItemClick', menuItem);

        if (menuItem.type !== EMenuType.CUSTOM && !isActiveNavItem(menuItem.link) && hoveredSecondLevelItem.value) {
            navigationStore.setActiveNavItems(props.menuItem, 1);
            navigationStore.setActiveNavItems(hoveredSecondLevelItem.value, 2);
            navigationStore.setActiveNavItems(menuItem, 3);
        }
        processMenuLink(menuItem);
    };

    const handleFirstLevelMenuItemMouseEnter = () => {
        isFirstLevelMenuItemHovered.value = true;
        showSecondLevelMenuDropdown.value = true;
    };

    const handleFirstLevelMenuItemMouseLeave = () => {
        isFirstLevelMenuItemHovered.value = false;
        hideSecondLevelMenu();
    };

    const handleSecondLevelMenuMouseEnter = () => {
        isSecondLevelMenuHovered.value = true;
    };

    const handleSecondLevelMenuItemMouseEnter = (secondLevelItem: INavItem, index: number) => {
        hoveredSecondLevelItem.value = secondLevelItem;
        hoveredSecondLevelItemIndex.value = index;
    };

    const handleSecondLevelMenuMouseLeave = () => {
        isSecondLevelMenuHovered.value = false;
        hoveredSecondLevelItem.value = null;
        hoveredSecondLevelItemIndex.value = 0;
        hideSecondLevelMenu();
    };

    const hideSecondLevelMenu = () => {
        setTimeout(() => {
            if ( !isFirstLevelMenuItemHovered.value && !isSecondLevelMenuHovered.value && !isThirdLevelMenuHovered.value) {
                showSecondLevelMenuDropdown.value = false;
                hoveredSecondLevelItem.value = null;
                hoveredSecondLevelItemIndex.value = 0;
            }
        }, 100);
    };

    const isSecondLevelItemHovered = (item: INavItem) => {
        return hoveredSecondLevelItem.value && hoveredSecondLevelItem.value.link === item.link;
    };

    const isThirdLevelItemHovered = (item: INavItem) => {
        return hoveredThirdLevelItem.value && hoveredThirdLevelItem.value.link === item.link;
    };

    const checkSpaceForMenuDropdowns = async () => {
        await nextTick();

        const hoveredPrimaryMenuItem = document.querySelector('.page-menu-link.hovered');
        const secondLevelMenu = document.querySelector('.dropdown-menu.second-level');
        const windowWidth = window.innerWidth;

        if (hoveredPrimaryMenuItem) {
            const rect = hoveredPrimaryMenuItem.getBoundingClientRect();
            isEnoughSpaceForSecondLevel.value = windowWidth - rect.left >= 250;
        }

        if (secondLevelMenu) {
            const rect = secondLevelMenu.getBoundingClientRect();
            isEnoughSpaceForThirdLevel.value = (windowWidth - rect.right) >= 250;
        }
    };

    const handleFirstLevelMenuItemFocus = () => {
        isFirstLevelMenuItemHovered.value = true;
        showSecondLevelMenuDropdown.value = true;
    };

    const handleFirstLevelMenuItemBlur = () => {
        isFirstLevelMenuItemHovered.value = false;
        hideSecondLevelMenu();
    };

    const handleSecondLevelMenuFocus = () => {
        isSecondLevelMenuHovered.value = true;
    };

    const handleSecondLevelMenuItemFocus = (secondLevelItem: INavItem, index: number) => {
        hoveredSecondLevelItem.value = secondLevelItem;
        hoveredSecondLevelItemIndex.value = index;
    };

    const handleSecondLevelMenuBlur = () => {
        isSecondLevelMenuHovered.value = false;
        hideSecondLevelMenu();
    };

    const handleThirdLevelMenuItemFocus = (thirdLevelItem: INavItem, index: number) => {
        hoveredThirdLevelItem.value = thirdLevelItem;
        focusedThirdLevelIndex.value = index; // Update the index if needed for keyboard navigation
    };

    const handleArrowNavigation = (event: any) => {
        const secondLevelItems = document.querySelectorAll('.first-level-index-' + props.firstLevelMenuIndex + ' .dropdown-item.menu-second-level');
        const thirdLevelItems = document.querySelectorAll('.first-level-index-' + props.firstLevelMenuIndex + ' .dropdown-item.menu-third-level');

        if (secondLevelItems.length > 0) {
            if (event.key === 'ArrowDown' && !isThirdLevelMenuHovered.value) {
                event.preventDefault();
                focusedSecondLevelIndex.value = (focusedSecondLevelIndex.value + 1) % secondLevelItems.length;
                hoveredSecondLevelItem.value = props.menuItem.items[focusedSecondLevelIndex.value];
            } else if (event.key === 'ArrowUp' && !isThirdLevelMenuHovered.value) {
                event.preventDefault();
                if (focusedSecondLevelIndex.value === 0) {
                    focusedSecondLevelIndex.value -= 1;
                    hoveredSecondLevelItem.value = null;
                    hoveredSecondLevelItemIndex.value = 0;
                    return;
                }

                focusedSecondLevelIndex.value = (focusedSecondLevelIndex.value - 1 + secondLevelItems.length) % secondLevelItems.length;
                hoveredSecondLevelItem.value = props.menuItem.items[focusedSecondLevelIndex.value];
            } else if (
                (event.key === 'ArrowLeft' && !isEnoughSpaceForThirdLevel.value)
                || (event.key === 'ArrowRight' && isEnoughSpaceForThirdLevel.value)
            ) {
                // Move to the third-level menu (from the second level)
                event.preventDefault();
                if (thirdLevelItems.length > 0) {
                    isThirdLevelMenuHovered.value = true;
                    isSecondLevelMenuHovered.value = true;
                    focusedThirdLevelIndex.value = 0;
                    hoveredThirdLevelItem.value = hoveredSecondLevelItem.value?.items[focusedThirdLevelIndex.value] ?? null;
                }
            } else if (event.key === 'Enter') {
                focusAndClickCurrentItem();
            }
        }

        if (thirdLevelItems.length > 0 && isSecondLevelMenuHovered.value) {
            if (event.key === 'ArrowDown' && isThirdLevelMenuHovered.value) {
                event.preventDefault();
                focusedThirdLevelIndex.value = (focusedThirdLevelIndex.value + 1) % (hoveredSecondLevelItem.value?.items?.length ?? 1);
                hoveredThirdLevelItem.value = hoveredSecondLevelItem.value?.items[focusedThirdLevelIndex.value] ?? null;

            } else if (event.key === 'ArrowUp' && isThirdLevelMenuHovered.value) {
                event.preventDefault();
                focusedThirdLevelIndex.value = (focusedThirdLevelIndex.value - 1 + thirdLevelItems.length) % thirdLevelItems.length;
                hoveredThirdLevelItem.value = hoveredSecondLevelItem.value?.items[focusedThirdLevelIndex.value] ?? null;
            }
            //Move back to the second-level menu (from the third level)
            else if (
                (event.key === 'ArrowRight' && !isEnoughSpaceForThirdLevel.value && isThirdLevelMenuHovered.value)
                || (event.key === 'ArrowLeft' && isEnoughSpaceForThirdLevel.value && isThirdLevelMenuHovered.value)
            ) {
                event.preventDefault();
                if (focusedSecondLevelIndex.value >= 0 && secondLevelItems[focusedSecondLevelIndex.value]) {
                    isThirdLevelMenuHovered.value = false;
                    isSecondLevelMenuHovered.value = false;
                    focusedThirdLevelIndex.value = -1;
                    hoveredThirdLevelItem.value = null;
                }
            } else if (event.key === 'Enter') {
                focusAndClickCurrentItem();
            }
        }
    };

    const focusAndClickCurrentItem = () => {
        // Logic to focus and click on the current item based on the current active index
        if (focusedSecondLevelIndex.value !== -1 && focusedThirdLevelIndex.value === -1) {
            const secondLevelItems = secondLevelItem.value;
            if (secondLevelItems && secondLevelItems[focusedSecondLevelIndex.value]) {
                (secondLevelItems[focusedSecondLevelIndex.value] as HTMLElement).focus();
                (secondLevelItems[focusedSecondLevelIndex.value] as HTMLElement).click();
                focusedSecondLevelIndex.value = -1;
                focusedThirdLevelIndex.value = -1;
                hoveredSecondLevelItem.value = null;
            }
        } else if (focusedThirdLevelIndex.value !== -1) {
            const thirdLevelItems = thirdLevelItem.value;
            if (thirdLevelItems && thirdLevelItems[focusedThirdLevelIndex.value]) {
                (thirdLevelItems[focusedThirdLevelIndex.value] as HTMLElement).focus();
                (thirdLevelItems[focusedThirdLevelIndex.value] as HTMLElement).click();
                focusedSecondLevelIndex.value = -1;
                focusedThirdLevelIndex.value = -1;
                hoveredThirdLevelItem.value = null;
            }
        }
    };

    onMounted(() => {
        checkSpaceForMenuDropdowns();
        document.addEventListener('keydown', handleArrowNavigation);
    });

    onUnmounted(() => {
        document.removeEventListener('keydown', handleArrowNavigation);
    });

    onUpdated(() => {
        checkSpaceForMenuDropdowns();
    });
</script>

<style scoped lang="scss">
    $buttonColor: v-bind(buttonColor);
    $buttonTextColor: v-bind(buttonTextColor);

    .page-menu-link,
    .page-menu-modal-link {
        @apply bg-white;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.07);
        transition: all 0.15s ease;

        &:hover {
            background-color: $buttonColor;
            color: $buttonTextColor;
        }
    }

    .page-menu-link,
    .page-menu-modal-link {
        overflow: hidden;
    }

    .page-menu-link-label,
    .page-menu-modal-link-label {
        line-height: 1.2;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3; /* Number of lines to display */
        overflow: hidden;
        text-overflow: ellipsis;
        letter-spacing: -0.5px;
    }

    .vertically-centered {
        display: flex;
        align-items: center;
    }

    .active,
    .page-menu-link.router-link-active {
        background-color: $buttonColor;
        color: $buttonTextColor;
    }

    .menu-icon-wrapper {
        @apply bg-white;
        width: 3.15rem;
        height: 3.15rem;
        margin-bottom: 0.5rem;
        padding: 0.7rem;
    }

    .icon-menu-item {
        display: contents;
        box-sizing: border-box;
    }

    #image-nav-dropdown {
        z-index: 1;
        background-color: #ffffff;
        border-radius: 0.4rem;
        display: inline-block;;
        position: absolute;
        top: 136px;
        transform: translateX(-62px);
        box-shadow: 0 1px 20px 0 rgba(0, 0, 0, 0.1);

        .dropdown-item {
            display: flex;
            gap: 0.5rem;
            padding: 9px 12px;
            font-size: 13px;
            text-decoration: none;
            width: 100%;
            align-items: center;
            color: #222;
            transition: all 0.15s ease;

            .dropdown-icon-wrap {
                @apply bg-white;
                width: 2rem;
                height: 2rem;
            }

            .right-icon {
                transition: all 0.15s ease;
                width: 13px;
            }

            &:hover {
                background-color: $buttonColor;
                color: $buttonTextColor;

                .right-icon {
                    transform: translateX(3px);
                    color: #ffffff;
                }
            }
        }

        .dropdown-item.hovered {
            background-color: $buttonColor;
            color: $buttonTextColor;

            .right-icon {
                transform: translateX(3px);
                color: #ffffff;
            }
        }

        .dropdown-item:not(:last-of-type) {
            border-bottom: 1px solid #e5eaf6;
        }

        .dropdown-item:first-child {
            border-top-left-radius: 0.4rem;
            border-top-right-radius: 0.4rem;
        }

        .dropdown-item-title {
            width: 160px;
            font-size: 0.825rem;
            font-weight: 600;
            line-height: 1.2;
            overflow: hidden;
            text-overflow: ellipsis;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
        }
    }

    .second-level .dropdown-item:nth-last-child(2) {
        border-bottom-left-radius: 0.4rem;
        border-bottom-right-radius: 0.4rem;
    }

    .third-level .dropdown-item:last-child {
        border-bottom-left-radius: 0.4rem;
        border-bottom-right-radius: 0.4rem;
    }

    #image-nav-dropdown.second-level {
        @media (max-width: 767px) {
            display: none;
        }
    }

    #image-nav-dropdown.third-level {
        transform: translateX(245px);
        top: 0;
        margin-top: 0;
    }

    .right-icon {
        transition: all 0.15s ease;
        color: $buttonColor;
    }

    .second-level-left-side {
        transform: translateX(-191px) !important;
    }

    .third-level-left-side {
        transform: translateX(-245px) !important;
    }

    .focused-second-level-item,
    .focused-third-level-item {
        .right-icon {
            transform: translateX(3px);
        }
    }

    .page-menu-link:focus-visible,
    .menu-dropdown .dropdown-menu.second-level:focus-visible {
        outline: 2px solid $buttonColor;
    }

    .menu-dropdown .dropdown-item.menu-second-level:focus-visible {
        outline: unset;
    }
</style>
